/**
 * 彩蛋页面访问权限验证工具
 * 参考旧项目的白名单验证逻辑
 */

import axios from 'axios';
import { getCookieAmend } from './apiUtils';

const LOCAL_STORAGE_KEY = 'wetv_linux_is_egg_page';
const WUJI_API_URL = 'https://cache.wuji.qq.com/x/api/wuji_cache/object';
const WUJI_PARAMS = {
  appid: 'video_international_conf_online',
  schemaid: 't_white_user_conf',
  schemakey: '3b5431e99e8d4c2f86f8ee18d2b55b14',
};

/**
 * 判断是否是开发环境或测试环境
 * 包括：开发环境、测试域名、URL 参数包含 debugger
 */
function isDevOrTestEnv(): boolean {
  // 仅支持 "development" 与测试域名或参数判断，避免类型兼容问题
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
    return true;
  }

  // 检查浏览器环境
  if (typeof window === 'undefined') return false;

  const { hostname, search } = window.location;

  // 检查 URL 参数中是否有 debugger
  if (new URLSearchParams(search).has('debugger')) {
    return true;
  }

  // 检查是否为测试域名
  if (hostname.includes('testlinux')) {
    return true;
  }
  if (hostname.includes('test.iflix.com') || hostname.includes('linuxtest.wetv.vip')) {
    return true;
  }

  return false;
}

/**
 * 检查 URL 参数是否包含 isEggPage=1
 */
export function checkUrlParam(): boolean {
  if (typeof window === 'undefined') return false;

  // 同时检查 search 和 hash 中的参数
  const searchParams = new URLSearchParams(window.location.search);
  const hashSearch = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '';
  const hashParams = new URLSearchParams(hashSearch);

  const result = searchParams.get('isEggPage') === '1' || hashParams.get('isEggPage') === '1';

  console.log('[easterEggAuth] checkUrlParam:', {
    search: window.location.search,
    hash: window.location.hash,
    searchParams: searchParams.get('isEggPage'),
    hashParams: hashParams.get('isEggPage'),
    result,
  });

  return result;
}

/**
 * 检查 localStorage 中是否有白名单标识
 * 如果发现存储的值与当前用户不匹配（用户切换了），返回 false，不清除旧标识
 * 让后续的无极接口检查来决定是否更新 localStorage
 */
export function checkLocalStorage(): boolean {
  if (typeof localStorage === 'undefined') return false;
  const vuid = getCookieAmend('vuserid');
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);

  console.log('[easterEggAuth] checkLocalStorage:', {
    vuid,
    stored,
    expected: vuid ? `white_user_${vuid}` : 'N/A',
    match: stored === `white_user_${vuid}`,
  });

  if (!vuid) {
    return false;
  }
  const expectedValue = `white_user_${vuid}`;
  if (stored && stored !== expectedValue) {
    return false;
  }

  return stored === expectedValue;
}

/**
 * 设置白名单标识到 localStorage
 */
export function setWhiteListFlag(): void {
  if (typeof localStorage === 'undefined') return;
  const vuid = getCookieAmend('vuserid');
  if (vuid) {
    localStorage.setItem(LOCAL_STORAGE_KEY, `white_user_${vuid}`);
  }
}

/**
 * 调用无极接口检查用户是否在白名单中
 */
export async function checkWujiWhiteList(): Promise<boolean> {
  const vuid = getCookieAmend('vuserid');
  if (!vuid) return false;

  try {
    const response = await axios.get(WUJI_API_URL, {
      params: {
        ...WUJI_PARAMS,
        filter: `c_vuid=${vuid}`,
      },
      timeout: 5000, // 5秒超时
    });

    if (response?.data?.data?.length > 0) {
      setWhiteListFlag();
      return true;
    }
    return false;
  } catch (error) {
    console.error('无极白名单接口调用失败:', error);
    return false;
  }
}

/**
 * 综合检查用户是否有权限访问彩蛋页面
 * 检查顺序: URL 参数 -> localStorage -> 无极接口
 */
export async function checkEasterEggAccess(): Promise<boolean> {
  // 1. 检查 URL 参数
  if (checkUrlParam()) {
    setWhiteListFlag();
    return true;
  }

  // 2. 检查 localStorage
  if (checkLocalStorage()) {
    return true;
  }

  // 3. 检查环境变量和测试域名，如果是开发环境或测试环境直接通过
  if (isDevOrTestEnv()) {
    return true;
  }

  // 4. 检查无极白名单(异步)
  return await checkWujiWhiteList();
}

/**
 * 在应用初始化时检查权限,如果有权限则跳转到彩蛋页面
 */
export async function initEasterEggCheck(): Promise<void> {
  if (typeof window === 'undefined') return;

  const urlParams = new URLSearchParams(window.location.search);
  const vuid = getCookieAmend('vuserid');

  // 如果 URL 参数中有 isEggPage=1
  if (urlParams.get('isEggPage') === '1') {
    setWhiteListFlag();

    // 如果不在彩蛋页面，才需要跳转
    // 项目使用 BrowserRouter，不需要手动跳转
    // 组件内的权限检查会自动处理访问控制
    console.log('[easterEggAuth] URL 参数验证通过，已设置白名单');
    return;
  }

  // localStorage 检查
  if (checkLocalStorage()) {
    // 已经在白名单中,不需要跳转
    return;
  }

  // 无极接口检查(异步,不阻塞应用启动)
  if (vuid) {
    checkWujiWhiteList().catch((error) => {
      console.error('无极白名单检查失败:', error);
    });
  }
}
